import { PreviouslyEmailedRecipient } from "../types";

/**
 * Whether excluding this person would drop only redundant email.
 *
 * sentForCurrentTarget is what keeps anyone still owed an email - someone invited to
 * Engineering last week but not yet to Product would otherwise lose the Product invitation
 * entirely. How long ago the send happened is deliberately not part of this: an admin
 * excluding previously emailed people wants everyone whose send would be redundant,
 * however long ago they were emailed.
 */

export const getUserAccountIdsToExclude = (
  previouslyEmailed: Record<number, PreviouslyEmailedRecipient>
) =>
  Object.values(previouslyEmailed)
    .filter((recipient) => recipient.sentForCurrentTarget)
    .map((recipient) => recipient.userAccountId);

/**
 * The label shown beside a recipient who has been emailed before.
 *
 * When only some of what they are owed has been sent, the label has to lead with what is
 * still missing. Leading with the date instead would read as "already handled" next to
 * someone who has never been told about the team or scan they are being invited to now.
 */
export const getPreviouslyEmailedLabel = (
  recipient: PreviouslyEmailedRecipient,
  formatDate: (isoDate: string) => string
): string => {
  if (recipient.sentForCurrentTarget) {
    return `Emailed ${formatDate(
      recipient.oldestTargetEmailedAt ?? recipient.lastEmailSent
    )}`;
  }

  const missingNames = recipient.pendingTargets
    .filter((target) => !target.alreadyEmailed)
    .map((target) => target.teamName)
    .filter((name): name is string => !!name);

  if (!missingNames.length) {
    return "Not yet emailed about part of this";
  }

  return `Still needs ${missingNames.join(", ")}`;
};
